import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Wifi, Car, Users, Zap, ArrowUpRight, MapPin } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

/* ── Shared helpers ── */
const Eyebrow = ({ children, light = false }) => (
  <p className={`font-mono text-[13px] uppercase tracking-[0.12em] ${light ? 'text-white/60' : 'text-[#D64545]'}`}>
    {children}
  </p>
);

/* Light grid backdrop used behind banner content */
const GridBackdrop = () => (
  <div
    className="absolute inset-0 pointer-events-none select-none"
    style={{
      backgroundImage: 'linear-gradient(to right,#D9D9D9 1px,transparent 1px),linear-gradient(to bottom,#D9D9D9 1px,transparent 1px)',
      backgroundSize: '100px 100px',
      opacity: 0.3
    }}
  />
);


/* Decorative kumiko lattice watermark used in banner corners */
const LatticePattern = ({ className = '', rows = 4, cols = 4, cell = 64 }) => {
  const width = cols * cell + 20;
  const height = rows * cell + 20;
  return (
    <svg
      width={width} height={height} viewBox={`0 0 ${width} ${height}`}
      aria-hidden="true"
      className={`absolute pointer-events-none ${className}`}
    >
      {Array.from({ length: rows }).flatMap((_, row) =>
        Array.from({ length: cols }).map((_, col) => {
          const x = col * cell + 10;
          const y = row * cell + 10;
          const s = cell - 14;
          return (
            <g key={`${row}-${col}`} opacity="0.22">
              <rect x={x} y={y} width={s} height={s} fill="none" stroke="#D64545" strokeWidth="1.5" />
              <line x1={x + s / 2} y1={y} x2={x + s / 2} y2={y + s} stroke="#D64545" strokeWidth="1.5" />
              <line x1={x} y1={y + s / 2} x2={x + s} y2={y + s / 2} stroke="#D64545" strokeWidth="1.5" />
            </g>
          );
        })
      )}
    </svg>
  );
};


const CASE_ICONS = [Wifi, Car, Users, Zap];

const FACILITY_STATICS = [
  { image: '/Bangalore.jpg', upcoming: false },
  { image: '/Kyoto.png',     upcoming: false },
  { image: '/Tenkasi.jpg',   upcoming: false },
  { image: '/Chennai.jpg',   upcoming: true  },
];

/* ── Data ── */
const leaders = [
  { name: 'Britto EV',        role: 'Founder & MD', accent: 'blue',  image: '/people/BrittoSan.png',   bio: 'Electrical Engineer 20+ years with Japanese companies. Well connected with the Japanese motor ecosystem. Believes in Indian innovation.', linkedin:'https://www.linkedin.com/in/britto-edward-victor-7463001/' },
  { name: 'Aravind Prasad',  role: 'Founder',       accent: 'blue', image: '/people/AravindSan.png',  bio: '20+ years in power electronics and motor controls. An innovator, he created the BLDC ceiling fan solution which has more than 50% share in India.', linkedin:'https://www.linkedin.com/in/aravind-prasad-sounderraj-8030818/' },
  { name: 'Prasanth B',       role: 'Founder',      accent: 'blue',  image: '/people/PrasanthSan.png', bio: 'Powertrain System Engineer for 14+ years who profess in Model Based Design. He has worked on design and development of diesel engine, transmissions, electric powertrains.', linkedin:'https://www.linkedin.com/in/prasanth-b-4267145a/' },
  { name: 'Mohara Toshinari', role: 'Director',     accent: 'red',  image: '/people/MoharaSan.png', bio: 'Seasoned powertrain engineer, who joined the FCC board from Honda R&D. A visionary leader, currently he heads the CASE initiative.', linkedin:'https://www.linkedin.com/in/%E6%95%8F%E6%88%90-%E8%8C%82%E5%8E%9F-336214299/' },
  { name: 'Sandeep Janardan', role: 'Founder',      accent: 'blue',  image: '/people/SandeepSan.png', bio: 'Product Manager. 20+ years at SAP, leading enterprise cloud and mobility innovation.', linkedin: 'https://www.linkedin.com/in/sandeepjanardan/' },
  { name: 'John Edward',      role: 'Founder',      accent: 'blue',image: '/people/JohnSan.jpg',     bio: '18+ years architecting IoT and cloud-native platforms at Honeywell, with expertise in device integration and secure automation. Passionate about bridging software architecture with next-generation motion control systems.', linkedin: 'https://www.linkedin.com/in/john-edward-mdu/' }
];
const ACCENTS = {
  blue: { borderColor: '#0042B8', nameColor: '#0042B8', roleColor: '#0042B8', overlayBg: '#E8E9F2' },
  red:  { borderColor: '#D64545', nameColor: '#E6001A', roleColor: '#D64545', overlayBg: '#F2E8E8' },
};
const staff = [
  { name: 'Arockiya lipson',  role: 'Hardware Design Engineer',      image: '/people/Arockiya%20lipson.png'  },
  { name: 'Senthilkumar S',   role: 'Vehicle Engineer',              image: '/people/Senthilkumar%20S.png'  },
  { name: 'Niraj Kishore',    role: 'Sr. Design Engineer',           image: '/people/Niraj%20Kishore.png'    },
  { name: 'Venkatesh U',      role: 'Industrial Designer',           image: '/people/Venakatesh%20U.png'     },
  { name: 'Akshat Sharma',    role: 'Software Engineer',             image: '/people/Akshat%20Sharma.png'    },
  { name: 'Katchi Mydeen K',  role: 'Hardware Design Engineer',      image: '/people/Katchi%20Mydeen%20K.png' },
  { name: 'Hariharan R',      role: 'Mechanical Design Engineer',    image: '/people/Hariharan%20R.png'      },
  { name: 'Shruthi B',        role: 'Design Engineer',               image: '/people/Shruthi%20B.png'        },
  { name: 'Muhammed Irish',   role: 'Supplier Quality Engineer',     image: '/people/Muhammed%20Irish.png'   },
];


const facilities = [
  { name: 'Takumi Bengaluru', badge: 'Bengaluru, India', desc: 'Engineering and PoC & Proto Production',        image: '/Bangalore.jpg', upcoming: false },
  { name: 'Takumi Kyoto',     badge: 'Kyoto, Japan',     desc: 'Technology Partnerships · Market Research',    image: '/Kyoto.png',    upcoming: false },
  { name: 'Takumi Tenkasi',   badge: 'Tenkasi, India',   desc: 'Engineering, Production & Test Lab',           image: '/Tenkasi.jpg',  upcoming: false },
  { name: 'Takumi Chennai',   badge: 'Chennai, India',   desc: 'Upcoming',                                     image: '/Chennai.jpg',  upcoming: true  },
];

const semiconductorPartners = [
  { logo: '/RenesasLogo.png',            name: 'Renesas',            category: 'Microcontrollers & Power' },
  { logo: '/InfineonLogo.png',           name: 'Infineon',           category: 'Power Semiconductors'    },
  { logo: '/TexasInstrumentsLogo.png',   name: 'Texas Instruments',  category: 'Analog & Industrial'     },
  { logo: '/NXPLogo.png',                name: 'NXP',                category: 'Automotive & IoT'        },
  { logo: '/MicrochipLogo.png',          name: 'Microchip',          category: 'MCU & Analog'            },
  { logo: '/OnsemiLogo.png',             name: 'onsemi',             category: 'Power & Sensing'         },
  { logo: '/ROHMLogo.png',               name: 'ROHM',               category: 'Power Management'        },
  { logo: '/StmicroelectronicsLogo.png', name: 'STMicroelectronics', category: 'Analog & Power'          },
];

const softwarePartners = [
  { logo: '/PTCLogo.png',              name: 'PTC',               category: 'Core CAD'              },
  { logo: '/AltiumLogo.png',           name: 'Altium',            category: 'PCB Design'            },
  { logo: '/TexasInstrumentsLogo.png', name: 'Texas Instruments', category: 'Analog & Industrial'   },
  { logo: '/TektronixLogo.png',        name: 'Tektronix',         category: 'Test & Measurement'    },
  { logo: '/HIOKILogo.png',            name: 'HIOKI',             category: 'Electrical Measurement' },
  { logo: '/SolidworksLogo.png',       name: 'SolidWorks',        category: '3D CAD & Simulation'   },
  { logo: '/MathworksLogo.png',        name: 'MathWorks',         category: 'MBD & Simulation'      },
];

/* ── Reusable leader card with hover overlay ── */
const LeaderCard = ({ person, borderColor, nameColor, roleColor, overlayBg }) => {
  const [hovered, setHovered] = useState(false);
  const hasLink = Boolean(person.linkedin);
  return (
    <a
      href={hasLink ? person.linkedin : undefined}
      target={hasLink ? '_blank' : undefined}
      rel={hasLink ? 'noopener noreferrer' : undefined}
      className="relative overflow-hidden rounded-xl cursor-pointer lg:flex lg:flex-col lg:h-full block"
      style={{ border: `1px solid ${borderColor}` }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div className="aspect-square sm:aspect-[3/4] lg:aspect-auto lg:flex-1 overflow-hidden bg-[#E0E1E3]">
        <img src={person.image} alt={person.name} className="w-full h-full object-cover object-top" />
      </div>

      <div className="px-1.5 py-1.5 sm:px-4 sm:py-2 bg-[#E5E6ED] min-h-[40px] sm:min-h-0">
        <h3 className="font-bold text-[9px] sm:text-[16px] leading-tight" style={{ color: nameColor }}>
          {person.name}
        </h3>
        <p className="text-[7px] sm:text-[12px] font-normal mt-0.5 sm:mt-1" style={{ color: roleColor }}>
          {person.role}
        </p>
      </div>

      <div
        className="absolute inset-0 flex flex-col p-2 sm:p-4"
        style={{
          backgroundColor: overlayBg,
          transform: hovered ? 'translateY(0%)' : 'translateY(calc(100% - 52px))',
          opacity: hovered ? 1 : 0,
          transition: hovered
            ? 'transform 400ms ease-in-out, opacity 80ms ease-out'
            : 'transform 400ms ease-in-out, opacity 100ms ease-out 320ms',
          willChange: 'transform, opacity',
        }}
      >
        <div className="flex items-start justify-between gap-1 sm:gap-2">
          <div>
            <h3 className="font-bold text-[10px] sm:text-[16px] leading-tight" style={{ color: nameColor }}>
              {person.name}
            </h3>
            <p className="text-[7px] sm:text-[12px] font-normal mt-0.5 sm:mt-1" style={{ color: roleColor }}>
              {person.role}
            </p>
          </div>
          <ArrowUpRight
            className="rotate-arrow w-3.5 h-3.5 sm:w-5 sm:h-5 shrink-0 mt-0.5"
              style={{
              color: nameColor,
              transform: hovered ? 'rotate(45deg)' : 'rotate(0deg)',
              transition: 'transform 720ms ease-in-out',
            }}
          />
        </div>

        <div className="mt-auto pt-3 sm:pt-4">
          <p
            className="text-[8px] sm:text-[11px] leading-[1.35] sm:leading-[1.45] tracking-tight"
            style={{
              color: roleColor,
              opacity: hovered ? 1 : 0,
              transition: hovered ? 'opacity 0.3s ease 0.25s' : 'opacity 0.15s ease 0s',
            }}
          >
            {person.bio}
          </p>
        </div>
      </div>
    </a>
  );
};

/* ── Partner logo card — 224 × 145, 8px radius ── */
const PartnerCard = ({ logo, name, category }) => (
  <div
    className="flex flex-col items-center bg-white w-full min-h-[72px] sm:min-h-[120px] rounded-[8px] px-1.5 pt-2 pb-1 sm:px-4 sm:pt-4 sm:pb-2.5"
    style={{ border: '1px solid rgba(0, 66, 184, 0.3)' }}
  >
    <div className="flex items-center justify-center w-full flex-1">
      <img src={logo} alt={name} className="max-h-[26px] sm:max-h-[48px] lg:max-h-[64px] max-w-[50px] sm:max-w-[110px] lg:max-w-[150px] object-contain" />
    </div>
    <p className="text-[8px] sm:text-[12px] text-[#4A4A4A] text-center leading-tight mt-0.5 sm:mt-0">{category}</p>
  </div>
);

/* ── Location photo card ── */
const FacilityCard = ({ name, badge, desc, image }) => (
  <div className="rounded-xl overflow-hidden bg-white">
    {/* Image section */}
    <div className="relative h-[160px] sm:h-[210px]">
      {image && (
        <img src={image} alt={name} className="w-full h-full object-cover" />
      )}
      {/* Location badge */}
      <div className="absolute top-3 left-3">
        <span className="inline-flex items-center gap-1.5 bg-[#F1F2F6] text-[#0042B8] text-[12px] font-semibold px-2.5 py-1 rounded-full">
          <MapPin className="w-3 h-3 shrink-0" />
          {badge}
        </span>
      </div>
    </div>
    {/* Content section */}
    <div className="px-4 py-3 bg-white">
      <h3 className="font-bold text-[15px] text-[#0042B8] leading-tight">{name}</h3>
      <p className="text-[12px] text-[#4A4A4A] mt-1 leading-snug">{desc}</p>
    </div>
  </div>
);

/* ── About page ── */
const AboutPage = () => {
  const { t } = useLanguage();
  const location = useLocation();

  useEffect(() => {
    const hash = location.hash?.slice(1);
    if (!hash) return;
    const el = document.getElementById(hash);
    if (el) setTimeout(() => el.scrollIntoView({ behavior: 'smooth' }), 100);
  }, [location.hash]);

  const caseItems = [
    { letter: 'C', label: t('about.case.connect'),    Icon: CASE_ICONS[0] },
    { letter: 'A', label: t('about.case.autonomous'), Icon: CASE_ICONS[1] },
    { letter: 'S', label: t('about.case.shared'),     Icon: CASE_ICONS[2] },
    { letter: 'E', label: t('about.case.electric'),   Icon: CASE_ICONS[3] },
  ];

  const facilityLocations = t('about.facilities.locations');
  const facilities = FACILITY_STATICS.map((f, i) => ({ ...f, ...facilityLocations[i] }));

  return (
  <div data-testid="about-page" className="pt-[88px] lg:pt-[64px] overflow-x-hidden">

    {/* ── 1. Banner ── */}
    <section id="company" className="relative bg-[#ECECF2] overflow-hidden py-8 lg:py-16 border-b border-[#D2D2D2]">
      <GridBackdrop />

      {/* ── Honeycomb pattern tiles ── */}
      {/* 1. Top-right */}
      <img
        src="/Takumi%20honeycomb%20pattern.png"
        aria-hidden="true"
        className="hidden sm:block absolute pointer-events-none select-none z-0"
        style={{ left: '64%', top: '-14%', width: '15%', aspectRatio: '1 / 1' }}
      />
      {/* 2. Bottom-right (left tile) — touches right tile */}
      <img
        src="/Takumi%20honeycomb%20pattern.png"
        aria-hidden="true"
        className="hidden sm:block absolute pointer-events-none select-none z-0"
        style={{ right: '10%', top: '70%', width: '15%', aspectRatio: '1 / 1' }}
      />
      {/* 2b. Bottom-right (right tile) */}
      <img
        src="/Takumi%20honeycomb%20pattern.png"
        aria-hidden="true"
        className="hidden sm:block absolute pointer-events-none select-none z-0"
        style={{ right: '-5%', top: '70%', width: '15%', aspectRatio: '1 / 1' }}
      />
      {/* 3. Bottom-left */}
      <img
        src="/Takumi%20honeycomb%20pattern.png"
        aria-hidden="true"
        className="hidden sm:block absolute pointer-events-none select-none z-0"
        style={{ left: '-7%', bottom: '-10%', width: '15%', aspectRatio: '1 / 1' }}
      />

      <div className="relative z-10 max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 pb-4">
        <h1 className="font-display leading-[0.95] tracking-tight" style={{ fontSize: 'clamp(2.5rem, 13vw, 130px)' }}>
          <span className="text-[#555555]">{t('about.banner.heading1')}</span>
          {' '}
          <span className="text-[#D0D0D0]">{t('about.banner.heading2')}</span>
        </h1>
        <p className="mt-6 text-[16px] sm:text-[20px] text-black leading-relaxed max-w-[540px]">
          {t('about.banner.description')}
        </p>
      </div>
    </section>

    {/* ── 2. Mission / Vision / CASE ── */}
    <section className="relative bg-[#D64545] text-white py-10 lg:py-28 overflow-hidden">
      {/* White grid backdrop */}
      <div
        aria-hidden="true"
        className="absolute inset-0 pointer-events-none select-none"
        style={{
          backgroundImage: 'linear-gradient(to right,rgba(255,255,255,0.12) 1px,transparent 1px),linear-gradient(to bottom,rgba(255,255,255,0.12) 1px,transparent 1px)',
          backgroundSize: '100px 100px',
        }}
      />

      <div className="relative z-10 max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8">

        {/* Our Mission */}
        <h2 className="font-display leading-[1.0] mb-8" style={{ fontSize: 'clamp(2.5rem, 11vw, 90px)' }}>{t('about.mission.title')}</h2>

        {/* Quote box — decreased padding, bold text, side-by-side quotes */}
        <div className="bg-[#F0F0F0] rounded-xl pt-5 px-5 pb-1 max-w-[620px]">
          <div className="flex items-start gap-3">
            <span className="text-[44px] font-bold text-[#2A2A2A] leading-[0.9] shrink-0 mt-1 select-none">&ldquo;</span>
            <p className="text-[16px] font-bold text-[#0F0F0F] leading-relaxed">
              {t('about.mission.quote')}
            </p>
          </div>
          <div className="text-right -mt-4">
            <span className="text-[36px] font-bold text-[#2A2A2A] leading-[0.9] select-none">&rdquo;</span>
          </div>
        </div>

        {/* We believe paragraph */}
        <p className="mt-6 text-[18px] text-white/80 leading-relaxed max-w-[620px]">
          {t('about.mission.belief')}
        </p>

        {/* Our Vision — right side, aligned with Team heading col */}
        <div className="mt-10 lg:pl-[580px]">
          <h2 className="font-display leading-[1.0] mb-6" style={{ fontSize: 'clamp(2.5rem, 11vw, 90px)' }}>{t('about.vision.title')}</h2>
          <p className="text-[16px] sm:text-[18px] text-white/80 leading-relaxed">
            {t('about.vision.description')}
          </p>
        </div>

        {/* Democratizing CASE Mobility */}
        <div className="mt-16">
          <h3 className="font-display leading-[1.0] text-white mb-8" style={{ fontWeight: 400, fontSize: 'clamp(1.5rem, 6vw, 40px)' }}>
            {t('about.case.title')}
          </h3>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {caseItems.map(({ letter, label, Icon }) => (
              <div
                key={letter}
                className="relative overflow-hidden bg-[#EEEFF1] rounded-lg h-[125px] sm:h-[280px] lg:h-[340px]"
              >
                {/* Icon + label — top */}
                <div className="p-4 sm:p-5 relative z-10">
                  <Icon className="w-5 h-5 sm:w-6 sm:h-6 text-[#D64545] mb-2" />
                  <p className="text-[#D64545] text-[14px] sm:text-[18px] font-semibold leading-tight">{label}</p>
                </div>
                {/* Oversized letter — bottom, slightly raised on mobile */}
                <span
                  aria-hidden="true"
                  className="absolute bottom-[-13px] left-[-6px] sm:bottom-[-43px] sm:left-[-20px] font-display font-black leading-[1] text-[#D64545] select-none pointer-events-none text-[78px] sm:text-[250px] lg:text-[340px]"
                >
                  {letter}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>

    {/* ── 3. Team ── */}
    <section id="team" className="relative bg-[#EEEFF1] py-10 lg:py-28 overflow-visible">
      <GridBackdrop />

      {/* TakumiT decorative — wide, spans team section + full facilities section below */}
      <img
        src="/TakumiT.png"
        alt=""
        aria-hidden="true"
        className="absolute pointer-events-none select-none z-0"
        style={{
          top: '250px',
          bottom: '-750px',
          right: '-80px',
          width: '680px',
          objectFit: 'fill',
          filter: 'invert(16%) sepia(97%) saturate(2748%) hue-rotate(217deg) brightness(97%) contrast(103%)',
          opacity: 0.40,
        }}
      />

      <div className="relative z-10 max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8">

        {/* ── Leadership: fixed-width cards + heading ── */}
        <div className="grid gap-1.5 lg:gap-8 mb-8 lg:mb-20 items-start lg:items-stretch grid-cols-3 lg:grid-cols-[repeat(3,160px)_1fr_1fr]">

          {/* Row 1 — col 1-3: first three leaders (blue) */}
          {leaders.slice(0, 3).map((person) => (
            <LeaderCard
              key={person.name}
              person={person}
              {...ACCENTS[person.accent]} 
            />
          ))}

          {/* Row 1 — col 4-5: heading */}
          <div className="col-span-3 lg:col-span-2 flex flex-col justify-start order-first sm:order-none">
            <p className="font-mono text-[14px] sm:text-[18px] tracking-[0.12em] text-[#D64545]">{t('about.team.eyebrow')}</p>
            <h2 className="mt-3 font-display font-black text-[32px] sm:text-[44px] lg:text-[56px] leading-[1.1] text-[#0042B8]">
              {t('about.team.heading')}
            </h2>
            <p className="mt-4 text-[14px] text-[#000000] leading-relaxed">
              {t('about.team.description')}
            </p>
          </div>

          {/* Row 2 — col 1-3: last three leaders (red) */}
          {leaders.slice(3).map((person) => (
            <LeaderCard
              key={person.name}
              person={person}
              {...ACCENTS[person.accent]} 
            />
          ))}

          {/* Row 2 — col 4-5: spacer */}
          <div aria-hidden="true" className="col-span-3 lg:col-span-2" />
        </div>

        {/* ── 40+ Minds: left heading + right photo grid ── */}
        <div className="grid lg:grid-cols-[3fr_3fr] gap-8 lg:gap-16 items-start">

          {/* Left: text content */}
          <div>
            <p className="font-mono text-[13px] tracking-[0.12em] text-[#D64545]">{t('about.team.peopleEyebrow')}</p>
            <h2 className="mt-3 font-display font-black text-[32px] sm:text-[44px] lg:text-[56px] leading-[1.1] text-[#2C5AA0]">
              {t('about.team.peopleHeading')}
            </h2>
            <p className="mt-4 text-[15px] text-[#000000] leading-relaxed">
              {t('about.team.peopleDescription')}
            </p>
            <div className="flex items-center gap-2 mt-8">
              <div className="w-10 h-[3px] bg-[#D64545]" />
              <div className="w-10 h-[3px] bg-[#D2D2D2]" />
              <div className="w-10 h-[3px] bg-[#D2D2D2]" />
            </div>
          </div>

          {/* Right: staff photo grid — 5 cols × 3 rows, staggered */}
          <div className="hidden grid-cols-5 gap-3">
            {[
              /* Row 1: _ S0 S1 S2 _  (3 centered) */
              null,      staff[0], staff[1], staff[2], null,
              /* Row 2: S3 S4 _ S5 S6 (gap at col 3) */
              staff[3],  staff[4], null,     staff[5], staff[6],
              /* Row 3: _ S7 _ S8 _  (2 centered) */
              null,      staff[7], null,     staff[8], null,
            ].map((member, i) =>
              member ? (
                <div key={i} className="px-2 py-2 bg-white">
                  <p className="text-[12px] font-semibold text-[#0F0F0F] leading-tight">{member.name}</p>
                  <p className="text-[12px] text-[#4A4A4A] mt-0.5 leading-tight">{member.role}</p>
                </div>
              ) : (
                <div key={i} aria-hidden="true"/>
              )
            )}
          </div>
        </div>

      </div>
    </section>

    {/* ── 4. Facilities ── */}
    <section id="facilities" className="relative bg-[#D64545] py-10 lg:py-28 overflow-hidden">
      {/* ── Floral pair 1: top-right corner, diagonal top-right → bottom-left ── */}
      {/* medium — lower-left along diagonal */}
      <img
        src="/floral%20pattern.png"
        aria-hidden="true"
        className="absolute pointer-events-none select-none opacity-20 top-[20px] sm:top-[90px] right-[60px] sm:right-[120px] w-[70px] sm:w-[90px] h-[115px] sm:h-[150px] object-contain"
      />
      {/* small — at top-right corner */}
      <img
        src="/floral%20pattern.png"
        aria-hidden="true"
        className="absolute pointer-events-none select-none opacity-[0.15] top-[6px] sm:top-[16px] right-[6px] sm:right-[16px] w-[50px] sm:w-[60px] h-[84px] sm:h-[100px] object-contain"
      />
      {/* ── Floral pair 2: near Chennai card, same diagonal direction ── */}
      {/* medium — lower-left, close to Chennai card right edge */}
      <img
        src="/floral%20pattern.png"
        aria-hidden="true"
        className="absolute pointer-events-none select-none opacity-20"
        style={{ bottom: '-60px', left: '29%', width: '110px', height: '185px', objectFit: 'contain' }}
      />
      {/* small — upper-right along diagonal */}
      <img
        src="/floral%20pattern.png"
        aria-hidden="true"
        className="absolute pointer-events-none select-none opacity-[0.15]"
        style={{ bottom: '80px', left: '36%', width: '58px', height: '97px', objectFit: 'contain' }}
      />

      <div className="relative z-10 max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8">
        <Eyebrow light>{t('about.facilities.eyebrow')}</Eyebrow>
        <h2 className="mt-3 font-black text-[28px] sm:text-[36px] lg:text-[44px] leading-[1.1] text-white mb-4">{t('about.facilities.heading')}</h2>
        <p className="text-[15px] text-white/75 max-w-[560px] leading-relaxed mb-10">
          {t('about.facilities.description')}
        </p>

        {/* Top row: 3 cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
          {facilities.slice(0, 3).map((f) => (
            <FacilityCard key={f.name} {...f} />
          ))}
        </div>

        {/* Bottom row: Chennai (left-aligned) */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <FacilityCard {...facilities[3]} />
        </div>
      </div>
    </section>

    {/* ── 5. Partners ── */}
    <section id="partners" className="relative bg-[#EEEFF1] py-10 lg:py-28">
      <GridBackdrop />
      <div className="relative z-10 max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8">

        {/* Header — aligned with Team heading col and Vision */}
        <div className="mb-8 lg:mb-16 lg:pl-[580px]">
          <p className="font-mono text-[16px] sm:text-[22px] tracking-[0.12em] text-[#E71B2E]">{t('about.partners.eyebrow')}</p>
          <h2
            className="mt-2 font-display text-[28px] sm:text-[36px] lg:text-[47px] text-[#2C5AA0]"
            style={{ fontWeight: 700, lineHeight: '1.1', letterSpacing: '0.03em' }}
          >
            {t('about.partners.heading')}
          </h2>
          <p className="mt-4 text-[15px] text-[#000000] leading-relaxed max-w-[420px]">
            {t('about.partners.description')}
          </p>
        </div>

        {/* ── Semiconductor Partners ── */}
        <div className="mb-8 lg:mb-14">
          <h3 className="text-[20px] sm:text-[28px] lg:text-[35px] font-semibold text-[#D64545] mb-4 lg:mb-6 leading-[1.3]">{t('about.partners.semiconductor')}</h3>
          <div className="grid grid-cols-4 sm:grid-cols-3 lg:grid-cols-5 gap-2 sm:gap-4">
            {semiconductorPartners.map((p) => (
              <PartnerCard key={p.name} {...p} />
            ))}
          </div>
        </div>

        {/* ── Software & Tools Partners ── */}
        <div className="mb-8 lg:mb-14">
          <h3 className="text-[20px] sm:text-[28px] lg:text-[35px] font-semibold text-[#D64545] mb-4 lg:mb-6 leading-[1.3]">{t('about.partners.software')}</h3>
          <div className="grid grid-cols-4 sm:grid-cols-3 lg:grid-cols-5 gap-2 sm:gap-4">
            {softwarePartners.map((p) => (
              <PartnerCard key={p.name} {...p} />
            ))}
          </div>
        </div>

        {/* ── Investors in Takumi ── */}
        <div>
          <h3 className="text-[20px] sm:text-[28px] lg:text-[35px] font-semibold text-[#D64545] mb-4 lg:mb-6 leading-[1.3]">{t('about.partners.investors')}</h3>
          <div className="flex flex-col sm:flex-row gap-4">

            {/* FCC */}
            <div
              className="flex overflow-hidden w-full sm:w-[440px]"
              style={{ minHeight: '120px', borderRadius: '9.8px', border: '1px solid rgba(0, 66, 184, 0.3)', backgroundColor: '#EEEFF1' }}
            >
              <div className="flex items-center justify-center shrink-0 w-[120px] sm:w-[170px]">
                <img src="/FCCLogo.png" alt="F.C.C." className="max-h-[90px] sm:max-h-[130px] max-w-[110px] sm:max-w-[145px] object-contain" />
              </div>
              <div className="flex flex-col justify-center px-4 sm:px-6 py-4">
                <p className="text-[14px] sm:text-[16px] font-semibold text-[#4A4A4A] mb-2">{t('about.partners.fccYear')}</p>
                <p className="text-[12px] text-[#4A4A4A] leading-snug" style={{ fontWeight: 500 }}>
                  {t('about.partners.fccDesc')}
                </p>
              </div>
            </div>

            {/* Zoho */}
            <div
              className="flex overflow-hidden w-full sm:w-[440px]"
              style={{ minHeight: '120px', borderRadius: '9.8px', border: '1px solid rgba(0, 66, 184, 0.3)', backgroundColor: '#EEEFF1' }}
            >
              <div className="flex items-center justify-center shrink-0 w-[120px] sm:w-[170px]">
                <img src="/ZohoLogo.png" alt="Zoho" className="max-h-[70px] sm:max-h-[90px] max-w-[100px] sm:max-w-[130px] object-contain" />
              </div>
              <div className="flex flex-col justify-center px-4 sm:px-6 py-4">
                <p className="text-[14px] sm:text-[16px] font-semibold text-[#4A4A4A] mb-2">{t('about.partners.zohoYear')}</p>
                <p className="text-[12px] text-[#4A4A4A] leading-snug" style={{ fontWeight: 500 }}>
                  {t('about.partners.zohoDesc')}
                </p>
              </div>
            </div>

          </div>
        </div>

      </div>
    </section>

    {/* ── 6. Contact CTA ── */}
    <section className="relative bg-[#E5E6ED] overflow-hidden py-10 sm:py-20 lg:py-0 lg:h-[425px] flex items-center">
      {/* Whiplash T line — right side decoration */}
      <img
        src="/Whiplash T line.png"
        alt=""
        aria-hidden="true"
        className="block absolute w-auto pointer-events-none select-none right-[-8%] sm:right-[-6%] top-0 sm:top-[-10%] h-[150%] sm:h-[180%]"
        style={{ objectFit: 'contain', objectPosition: 'right center', filter: 'brightness(0) saturate(100%) invert(36%) sepia(94%) saturate(651%) hue-rotate(322deg) brightness(105%)' }}
      />

      <div className="relative z-10 w-full max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="font-black text-[26px] sm:text-[44px] lg:text-[52px] leading-[1.1] text-[#D64545]">
          {t('about.cta.heading')}
        </h2>
        <p className="mt-6 text-[16px] text-[#000000] leading-relaxed max-w-[420px]" style={{ fontWeight: 500 }}>
          {t('about.cta.description')}
        </p>
        <Link
          to="/contact"
          className="mt-10 inline-flex items-center px-8 py-3.5 border border-[#0F0F0F] text-[#0F0F0F] text-[15px] font-semibold hover:bg-[#BC2929] hover:text-white hover:border-[#BC2929] active:bg-[#0042B8] active:text-[#EEEFF1] active:border-[#0042B8] transition-colors duration-200"
        >
          {t('about.cta.button')}
        </Link>
      </div>
    </section>

  </div>
  );
};

export default AboutPage;
